import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Phone, Navigation2, X, Image as ImageIcon, RotateCcw, HandCoins, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { CameraCapture } from "../../components/driver/CameraCapture";
import { PodViewerDialog } from "../../components/deliveries/PodViewerDialog";
import { queueDeliverAction, queueNotDeliveredAction, queueCollectCash, getLocalOverride, getCashOverride, getCachedRoute } from "../../lib/driverSync";
import { SyncIndicator } from "../../components/driver/SyncIndicator";
import { formatCurrency } from "../../lib/utils";

function stopMeta(status) {
  if (status === 2) return { label: "Delivered", color: "bg-success-bg text-success" };
  if (status === 3) return { label: "Not Delivered", color: "bg-danger-bg text-danger" };
  return { label: "Pending", color: "bg-warning-bg text-warning" };
}

function packingSkippedMeta() {
  return { label: "Skipped by Packing", color: "bg-danger-bg text-danger" };
}

function mapsLink(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

export default function DriverStopAction() {
  const { routeId, stopId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, stop: null, route: null });
  const [mode, setMode] = useState(null); // "delivered" | "not-delivered" | "cash"
  const [photo1, setPhoto1] = useState(null);
  const [photo2, setPhoto2] = useState(null);
  const [reason, setReason] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [podOpen, setPodOpen] = useState(false);
  const [siOpen, setSiOpen] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/driver/routes/${routeId}`)
      .then(({ data }) => {
        const stop = data.stops.find((s) => String(s.id) === String(stopId));
        if (!stop) throw new Error("not-found");
        setState({ loading: false, error: null, stop, route: data });
      })
      .catch(async (error) => {
        if (error?.message !== "not-found") {
          const cached = await getCachedRoute(routeId);
          const stop = cached?.stops?.find((s) => String(s.id) === String(stopId));
          if (stop) {
            setState({ loading: false, error: null, stop, route: cached });
            return;
          }
        }
        setState({ loading: false, error: error?.message === "not-found" ? "Stop not found." : getApiErrorMessage(error, "Could not load this stop."), stop: null, route: null });
      });
  }, [routeId, stopId]);

  // Auto-show the special instruction popup once when this stop opens.
  const hasSpecial = Boolean(state.stop?.special_instruction);
  useEffect(() => {
    if (hasSpecial) setSiOpen(true);
  }, [hasSpecial, stopId]);

  const submitDelivered = async () => {
    const isReplace = state.stop.delivery_status === 2;
    setSubmitting(true);
    try {
      await queueDeliverAction(stopId, photo1.blob, photo2.blob);
      toast.success(isReplace ? "Proof of delivery photos updated." : "Marked as Delivered.");
      navigate(`/driver/routes/${routeId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not mark as delivered."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitNotDelivered = async () => {
    if (!reason.trim()) {
      toast.error("Please enter a reason.");
      return;
    }
    setSubmitting(true);
    try {
      await queueNotDeliveredAction(stopId, reason.trim());
      toast.success("Marked as Not Delivered.");
      navigate(`/driver/routes/${routeId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not mark as not delivered."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitCollectCash = async () => {
    const amt = Number(cashAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await queueCollectCash(stopId, amt);
      toast.success(res.queued ? "Saved offline — will sync when back online." : "Cash collected.");
      navigate(`/driver/routes/${routeId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not record cash collection."));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="driver-stop-action-page">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-4 h-40 w-full" />
      </div>
    );
  }

  if (state.error || !state.stop) {
    return (
      <div data-testid="driver-stop-action-page">
        <ErrorState message={state.error || "Stop not found."} onRetry={() => navigate(`/driver/routes/${routeId}`)} />
      </div>
    );
  }

  const stop = state.stop;
  const route = state.route;
  const override = getLocalOverride(stop.id);
  const effectiveStatus = override?.delivery_status ?? stop.delivery_status;
  const skippedByPacking = stop.packing_status === 2;
  const meta = skippedByPacking ? packingSkippedMeta() : stopMeta(effectiveStatus);
  const canAct = !skippedByPacking && effectiveStatus === 1 && route?.started_at && !route?.finished_at && !mode;
  const hasCoords = stop.latitude && stop.longitude;
  const cashOverride = getCashOverride(stop.id);
  const cashCollected = cashOverride?.collected || stop.payment_state === "collected";
  const cashPending = !cashCollected && stop.payment_state === "cash_pending";

  return (
    <div data-testid="driver-stop-action-page">
      <SyncIndicator />
      <button type="button" onClick={() => navigate(`/driver/routes/${routeId}`)} className="mb-3 flex items-center gap-1 text-xs text-text-muted" data-testid="driver-stop-action-back-button">
        <ArrowLeft size={14} strokeWidth={1.5} /> Back to stops
      </button>

      {stop.special_instruction && (
        <button
          type="button"
          onClick={() => setSiOpen(true)}
          className="mb-4 flex w-full items-start gap-2 rounded-md border border-warning/40 bg-warning-bg px-3 py-2.5 text-left active:scale-[0.99]"
          data-testid={`driver-stop-special-banner-${stop.id}`}
        >
          <Megaphone size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-warning" />
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-warning">Special instruction</span>
            <span className="block truncate text-sm text-text-main">{stop.special_instruction}</span>
          </span>
          <span className="shrink-0 self-center text-[11px] font-medium text-warning">View</span>
        </button>
      )}

      <div className="mb-4 rounded-md border border-border bg-surface p-4" data-testid="driver-stop-profile-card">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            #{stop.delivery_order_id}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-text-main">{stop.customer_name || `Delivery ${stop.id}`}</p>
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.color}`} data-testid={`driver-stop-profile-status-${stop.id}`}>
              {meta.label}
            </span>
          </div>
        </div>

        <p className="mt-3 text-sm text-text-muted" data-testid={`driver-stop-profile-address-${stop.id}`}>{stop.address || "No address on file"}</p>

        <div className="mt-3 flex gap-2">
          {stop.phone && (
            <a
              href={`tel:${stop.phone}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-text-main active:bg-background"
              data-testid={`driver-stop-call-${stop.id}`}
            >
              <Phone size={15} strokeWidth={1.5} /> Call
            </a>
          )}
          {hasCoords && (
            <a
              href={mapsLink(stop.latitude, stop.longitude)} target="_blank" rel="noreferrer"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-text-main active:bg-background"
              data-testid={`driver-stop-navigate-${stop.id}`}
            >
              <Navigation2 size={15} strokeWidth={1.5} /> Navigate
            </a>
          )}
        </div>

        {effectiveStatus === 3 && (stop.not_delivered_reason || override?.not_delivered_reason) && (
          <p className="mt-3 flex items-start gap-1 text-xs text-danger" data-testid={`driver-stop-reason-${stop.id}`}>
            <X size={13} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" /> {override?.not_delivered_reason || stop.not_delivered_reason}
          </p>
        )}
      </div>

      {skippedByPacking && (
        <p className="mb-4 rounded-md border border-danger/30 bg-danger-bg p-3 text-center text-xs font-medium text-danger" data-testid="driver-stop-packing-skipped-note">
          This tiffin was skipped by the packing team — no delivery action is available for it.
        </p>
      )}

      {cashPending && mode !== "cash" && (
        <div className="mb-4 rounded-md border border-danger/40 bg-danger-bg p-3" data-testid="driver-stop-cash-pending">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-danger">
            <HandCoins size={16} strokeWidth={1.5} /> Cash — {formatCurrency(stop.cash_amount_due)} due
          </span>
          <Button
            variant="danger" className="mt-3 h-11 w-full"
            onClick={() => { setCashAmount(stop.cash_amount_due != null ? String(stop.cash_amount_due) : ""); setMode("cash"); }}
            data-testid="driver-stop-collect-cash-button"
          >
            <HandCoins size={16} strokeWidth={1.5} /> Collect Cash
          </Button>
        </div>
      )}

      {cashCollected && (
        <div className="mb-4 flex items-center gap-1.5 rounded-md border border-success/40 bg-success-bg p-3 text-sm font-medium text-success" data-testid="driver-stop-cash-collected">
          <HandCoins size={16} strokeWidth={1.5} /> Cash Collected{cashOverride?.amount ? ` — ${formatCurrency(cashOverride.amount)}` : ""}
        </div>
      )}

      {mode === "cash" && (
        <div className="mt-4 space-y-3" data-testid="driver-stop-cash-flow">
          <Label htmlFor="cash-amount">Amount collected</Label>
          <Input
            id="cash-amount" type="number" min="0" step="0.01" inputMode="decimal"
            value={cashAmount} onChange={(e) => setCashAmount(e.target.value)}
            data-testid="driver-stop-cash-amount-input"
          />
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setMode(null)} data-testid="driver-stop-cash-cancel-button">
              Cancel
            </Button>
            <Button className="flex-1" disabled={submitting} onClick={submitCollectCash} data-testid="driver-stop-cash-submit-button">
              {submitting ? "Saving..." : "Confirm Collected"}
            </Button>
          </div>
        </div>
      )}

      {canAct && (
        <div className="space-y-2">
          <Button className="h-12 w-full" onClick={() => setMode("delivered")} data-testid="driver-stop-mark-delivered-button">
            <CheckCircle2 size={18} strokeWidth={1.5} /> Mark Delivered
          </Button>
          <button
            type="button" onClick={() => setMode("not-delivered")}
            className="flex w-full items-center justify-center gap-1.5 py-2 text-xs font-medium text-danger"
            data-testid="driver-stop-mark-not-delivered-button"
          >
            <XCircle size={14} strokeWidth={1.5} /> Unable to Deliver
          </button>
        </div>
      )}

      {!canAct && !mode && effectiveStatus === 1 && !skippedByPacking && (
        <p className="text-center text-xs text-text-muted" data-testid="driver-stop-inactive-note">
          {route?.finished_at ? "This route has finished." : "Start the route to update this stop."}
        </p>
      )}

      {effectiveStatus === 2 && !mode && (
        <div className="space-y-2">
          {(stop.pof1 || stop.pof2) && (
            <Button variant="outline" className="h-12 w-full" onClick={() => setPodOpen(true)} data-testid="driver-stop-view-pod-button">
              <ImageIcon size={18} strokeWidth={1.5} /> View Proof of Delivery
            </Button>
          )}
          <button
            type="button" onClick={() => setMode("delivered")}
            className="flex w-full items-center justify-center gap-1.5 py-2 text-xs font-medium text-text-muted"
            data-testid="driver-stop-replace-pod-button"
          >
            <RotateCcw size={14} strokeWidth={1.5} /> Made a mistake? Replace photos
          </button>
        </div>
      )}

      {mode === "delivered" && (
        <div className="mt-4 space-y-3" data-testid="driver-stop-delivered-flow">
          <p className="text-xs text-text-muted">
            {state.stop.delivery_status === 2 ? "Retake 2 photos to replace the proof of delivery. Both are required." : "Take 2 photos as proof of delivery. Both are required."}
          </p>
          <CameraCapture label="Photo 1" onCapture={(blob, url) => setPhoto1({ blob, url })} />
          {photo1 && <CameraCapture label="Photo 2" onCapture={(blob, url) => setPhoto2({ blob, url })} />}
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => { setMode(null); setPhoto1(null); setPhoto2(null); }} data-testid="driver-stop-delivered-cancel-button">
              Cancel
            </Button>
            <Button
              className="flex-1" disabled={!photo1 || !photo2 || submitting}
              onClick={submitDelivered} data-testid="driver-stop-delivered-submit-button"
            >
              {submitting ? "Submitting..." : "Submit Delivery"}
            </Button>
          </div>
        </div>
      )}

      {mode === "not-delivered" && (
        <div className="mt-4 space-y-3" data-testid="driver-stop-not-delivered-flow">
          <Label htmlFor="reason">Reason (required)</Label>
          <Textarea
            id="reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer not home, wrong address, gate locked..."
            rows={4} data-testid="driver-stop-not-delivered-reason-input"
          />
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => { setMode(null); setReason(""); }} data-testid="driver-stop-not-delivered-cancel-button">
              Cancel
            </Button>
            <Button
              variant="danger" className="flex-1" disabled={submitting}
              onClick={submitNotDelivered} data-testid="driver-stop-not-delivered-submit-button"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      )}

      <PodViewerDialog open={podOpen} onOpenChange={setPodOpen} delivery={{ id: stop.id, pof1: stop.pof1, pof2: stop.pof2 }} />

      <Dialog open={siOpen} onOpenChange={setSiOpen}>
        <DialogContent className="max-w-sm" data-testid="driver-special-instruction-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={18} strokeWidth={1.75} className="text-warning" /> Special Instruction
            </DialogTitle>
          </DialogHeader>
          <p className="rounded-md bg-warning-bg p-3 text-sm leading-relaxed text-text-main" data-testid="driver-special-instruction-text">
            {stop.special_instruction}
          </p>
          <p className="text-xs text-text-muted">For {stop.customer_name || `Delivery ${stop.id}`} — today only.</p>
          <Button className="w-full" onClick={() => setSiOpen(false)} data-testid="driver-special-instruction-ack">Got it</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
